import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown, Layers, Search } from "lucide-react";
import { useWorkspace, workspaceMeta } from "../context/WorkspaceContext";
import { useAccess } from "../context/AccessContext";
import "./WorkspaceSwitch.css";

// Search stays in the component. Hide it until workspace search is needed again.
const SHOW_WORKSPACE_SEARCH = false;

function WorkspaceIcon({ selected, size = 16 }) {
  return (
    <span className={`workspace-switch__icon${selected ? " workspace-switch__icon--active" : ""}`}>
      <Layers size={size} />
    </span>
  );
}

export default function WorkspaceSwitch() {
  const { teams, activeTeam, setTeamCode } = useWorkspace();
  const { isAdmin } = useAccess();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!panelRef.current?.contains(event.target)) close();
    };
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && isAdmin) searchRef.current?.focus();
  }, [open, isAdmin]);

  const departments = useMemo(() => {
    const term = query.trim().toLowerCase();
    const list = teams || [];
    if (!term) return list;
    return list.filter((team) => {
      const meta = workspaceMeta(team);
      return `${meta.name} ${meta.detail}`.toLowerCase().includes(term);
    });
  }, [teams, query]);

  if (!teams || teams.length < 2 || !activeTeam) return null;

  const current = workspaceMeta(activeTeam);

  const choose = (code) => {
    setTeamCode(code);
    close();
    if (location.pathname !== "/") {
      navigate("/");
    }
  };

  return (
    <div className="workspace-switch" ref={panelRef}>
      <button
        type="button"
        className="workspace-switch__trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => (open ? close() : setOpen(true))}
      >
        <WorkspaceIcon selected />
        <span className="workspace-switch__copy">
          <span className="workspace-switch__label">Workspace</span>
          <span className="workspace-switch__value">{current.name}</span>
        </span>
        <ChevronDown size={16} className={`workspace-switch__chevron${open ? " workspace-switch__chevron--open" : ""}`} />
      </button>
      {open && !isAdmin && (
        <div className="workspace-switch__panel" role="listbox" aria-label="Switch Workspace">
          <div className="workspace-switch__heading">Switch Workspace</div>
          <div className="workspace-switch__sub">Each workspace shows that role's resources.</div>
          <div className="workspace-switch__options">
            {teams.map((team) => {
              const meta = workspaceMeta(team);
              const selected = team.code === activeTeam.code;
              return (
                <button
                  key={team.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`workspace-switch__option${selected ? " workspace-switch__option--selected" : ""}`}
                  onClick={() => choose(team.code)}
                >
                  <WorkspaceIcon selected={selected} size={14} />
                  <span className="workspace-switch__option-copy">
                    <span className="workspace-switch__option-name">{meta.name}</span>
                    {meta.detail ? (
                      <span className="workspace-switch__option-detail">{meta.detail}</span>
                    ) : null}
                  </span>
                  {selected && <Check size={14} className="workspace-switch__check" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {open && isAdmin && (
        <div className="workspace-switch__panel workspace-switch__panel--admin" role="dialog" aria-label="Switch workspace">
          <div className="workspace-switch__heading">Switch workspace</div>
          <div className="workspace-switch__sub">Admin access · All departments</div>
          {SHOW_WORKSPACE_SEARCH && (
            <label className="workspace-switch__search">
              <Search size={14} />
              <input
                ref={searchRef}
                type="search"
                value={query}
                placeholder="Search departments..."
                aria-label="Search departments"
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
          )}
          {departments.length === 0 ? (
            <div className="workspace-switch__empty">No departments match.</div>
          ) : (
            <div className="workspace-switch__grid" role="listbox" aria-label="Departments">
              {departments.map((team) => {
                const meta = workspaceMeta(team);
                const selected = team.code === activeTeam.code;
                return (
                  <button
                    key={team.code}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`workspace-switch__card${selected ? " workspace-switch__card--selected" : ""}`}
                    onClick={() => choose(team.code)}
                  >
                    <WorkspaceIcon selected={selected} size={14} />
                    <span className="workspace-switch__option-copy">
                      <span className="workspace-switch__option-name">{meta.name}</span>
                      {meta.detail ? (
                        <span className="workspace-switch__option-detail">{meta.detail}</span>
                      ) : null}
                    </span>
                    {selected && <Check size={14} className="workspace-switch__check" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
